import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { WaveSine, CheckCircle, XCircle, SpinnerGap } from '@phosphor-icons/react';

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState({ status: 'loading' });
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const token = params.get('token');
    if (!token) { setState({ status: 'error', message: 'Missing verification token' }); return; }
    api.post('/auth/verify-email', { token })
      .then(r => {
        if (r.data.session_token) localStorage.setItem('session_token', r.data.session_token);
        setUser(r.data.user);
        setState({ status: 'ok' });
      })
      .catch(e => setState({ status: 'error', message: e.response?.data?.detail || 'Verification failed' }));
  }, [params, setUser]);

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
      <div data-testid="email-verify-card" className="w-full max-w-md border border-zinc-800 rounded-2xl bg-zinc-900/50 p-8 text-center">
        <Link to="/" className="flex items-center gap-2 mb-4 justify-center">
          <WaveSine size={22} weight="duotone" className="text-[#ccff00]" />
          <span className="font-display text-lg font-bold tracking-tight">TUNEAMPLIFIER</span>
        </Link>

        {state.status === 'loading' && (
          <div className="mt-4" data-testid="verify-email-loading">
            <SpinnerGap size={34} className="text-[#ccff00] mx-auto animate-spin" />
            <p className="mt-4 text-sm text-zinc-400 font-mono">Verifying your email…</p>
          </div>
        )}

        {state.status === 'ok' && (
          <div className="mt-4" data-testid="verify-email-success">
            <CheckCircle size={40} weight="fill" className="text-[#ccff00] mx-auto" />
            <h1 className="font-display text-3xl font-bold mt-4">You're in.</h1>
            <p className="text-sm text-zinc-400 mt-2">Your email is verified and you've got 10 free credits waiting.</p>
            <Button data-testid="verify-email-goto-dashboard" onClick={() => nav('/dashboard')} className="btn-neon rounded-full mt-6 h-11 px-6 font-display font-semibold">
              Go to dashboard
            </Button>
          </div>
        )}

        {state.status === 'error' && (
          <div className="mt-4" data-testid="verify-email-error">
            <XCircle size={40} weight="fill" className="text-red-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-4">Link didn't work</h1>
            <p className="text-sm text-zinc-400 mt-2">{state.message}</p>
            <Button data-testid="verify-email-goto-register" onClick={() => nav('/register')} className="rounded-full mt-6 h-11 px-6 border border-zinc-800 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:border-[#ccff00]/60 font-display">
              Back to sign up
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
